// src/pages/Rapports.js - Module de rapports transversal (Stock, Incidents, Abonnements, Quotas)
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
};

// Configuration de chaque domaine : où récupérer les données, comment les
// aplatir en lignes de tableau, et les colonnes à afficher/exporter.
const DOMAINES = {
  stock: {
    label: "Stock",
    icon: "bi-box-seam",
    endpoint: "/stock/produits/list_produit.php",
    extract: (data) => data?.data?.produits || [],
    columns: [
      { key: "libele", label: "Produit" },
      { key: "type_produit", label: "Catégorie" },
      { key: "quantite_stock", label: "Quantité" },
      { key: "seuil_alerte", label: "Seuil alerte", format: (v) => v ?? "-" },
      { key: "stock_faible", label: "Stock faible", format: (v) => (v ? "Oui" : "Non") },
      { key: "created_at", label: "Créé le" },
    ],
  },
  incidents: {
    label: "Incidents",
    icon: "bi-exclamation-triangle",
    endpoint: "/incidents/liste_incidents.php",
    extract: (data) => data?.data || [],
    columns: [
      { key: "numero_ticket_incident", label: "Ticket" },
      { key: "description_incident", label: "Description" },
      { key: "niveau", label: "Niveau", format: (v) => v?.label || "-" },
      { key: "type_incident", label: "Type", format: (v) => v?.type || "-" },
      { key: "statut", label: "Statut" },
      { key: "date_creer_incident", label: "Date création" },
    ],
  },
  abonnements: {
    label: "Abonnements",
    icon: "bi-credit-card",
    endpoint: "/abonnements/list.php",
    extract: (data) => data?.abonnements || [],
    columns: [
      { key: "nom_societe", label: "Société" },
      { key: "libele_abonnement", label: "Offre" },
      { key: "montant_abonnement", label: "Montant", format: (v) => `${v} XOF` },
      { key: "date_debut_abonnemt", label: "Début" },
      { key: "date_fin_abonnement", label: "Fin" },
      { key: "est_actif", label: "Actif", format: (v) => (v ? "Oui" : "Non") },
    ],
  },
  quotas: {
    label: "Quotas",
    icon: "bi-graph-up",
    endpoint: "/achat_quota/list.php",
    extract: (data) => data?.achats || [],
    columns: [
      { key: "societe", label: "Société" },
      { key: "type", label: "Type" },
      { key: "quantite", label: "Quantité" },
      { key: "date_achat", label: "Date d'achat" },
      { key: "expiration", label: "Expiration" },
      { key: "est_expire", label: "Expiré", format: (v) => (v ? "Oui" : "Non") },
    ],
  },
};

// L'admin ne gère pas le stock ni les incidents — données opérationnelles
// propres à chaque société — il ne voit que les rapports transversaux
// (abonnements, quotas). Le gestionnaire garde l'accès aux 4 domaines.
const DOMAINES_PAR_PROFIL = {
  1: ['abonnements', 'quotas'],
  3: ['stock', 'incidents', 'abonnements', 'quotas'],
};

const Rapports = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [domaine, setDomaine] = useState(null);
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [lignes, setLignes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [societeLogo, setSocieteLogo] = useState(null);
  const [nomSociete, setNomSociete] = useState("");
  const [societeInfoLigne, setSocieteInfoLigne] = useState("");

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (!stored) {
      navigate("/login");
      return;
    }
    try {
      const u = JSON.parse(stored);
      const parsed = { ...u, profile: Number(u.profile || u.poste || 0) };
      setUserInfo(parsed);
      const disponibles = DOMAINES_PAR_PROFIL[parsed.profile] || [];
      setDomaine(disponibles[0] || null);
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  const domainesAutorises = useMemo(
    () => DOMAINES_PAR_PROFIL[userInfo?.profile] || [],
    [userInfo]
  );

  // Logo de la société (absent pour l'admin, qui n'en a pas un seul propre) :
  // embarqué dans les PDF exportés ci-dessous.
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    axios.get(`${API_BASE_URL}/societes/my_logo.php`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (res.data?.logo_base64) setSocieteLogo(res.data.logo_base64);
        if (res.data?.nom_societe) setNomSociete(res.data.nom_societe);
        const ligne = [res.data?.address_societe, res.data?.tel_societe].filter(Boolean).join(" — ");
        if (ligne) setSocieteInfoLigne(ligne);
      })
      .catch(() => {});
  }, []);

  const fetchDonnees = useCallback(async () => {
    // Garde-fou : n'interroge jamais un domaine hors de ceux autorisés pour ce
    // profil (l'admin ne doit jamais récupérer stock/incidents, même si
    // `domaine` se retrouvait momentanément désynchronisé du profil).
    if (!domaine || !domainesAutorises.includes(domaine)) {
      setLignes([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("access_token");
      const config = DOMAINES[domaine];
      const response = await axios.get(`${API_BASE_URL}${config.endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let data = config.extract(response.data);

      // Filtre de date générique côté client (les endpoints n'ont pas tous
      // les mêmes noms de colonnes de date).
      const dateKey = domaine === "stock" ? "created_at"
        : domaine === "incidents" ? "date_creer_incident"
        : domaine === "abonnements" ? "date_debut_abonnemt"
        : "date_achat";

      if (dateDebut) {
        data = data.filter((row) => row[dateKey] && row[dateKey] >= dateDebut);
      }
      if (dateFin) {
        data = data.filter((row) => row[dateKey] && row[dateKey] <= dateFin + " 23:59:59");
      }

      setLignes(data);
    } catch (err) {
      setError(err.response?.data?.message || "Impossible de charger les données.");
      setLignes([]);
    } finally {
      setLoading(false);
    }
  }, [domaine, dateDebut, dateFin, domainesAutorises]);

  useEffect(() => {
    if (userInfo) fetchDonnees();
  }, [userInfo, fetchDonnees]);

  // Repli sur "abonnements" le temps très bref où `domaine` est encore null
  // (avant que userInfo ne soit chargé) — évite un crash de rendu, aucune
  // donnée n'est réellement chargée pour ce domaine tant que fetchDonnees ne
  // l'autorise pas (voir le garde-fou ci-dessus).
  const config = DOMAINES[domaine] || DOMAINES.abonnements;

  const cellValue = (row, col) => {
    const raw = row[col.key];
    return col.format ? col.format(raw) : (raw ?? "-");
  };

  const exportPDF = () => {
    if (lignes.length === 0) {
      alert("Aucune donnée à exporter.");
      return;
    }
    const doc = new jsPDF('l', 'mm', 'a4');

    // En-tête : logo à gauche, nom/adresse/tél. de la société alignés sur la
    // même ligne (à droite du logo, pas centrés en dessous), puis le titre du
    // rapport sous l'ensemble, puis un séparateur avant le reste du contenu.
    const logoSize = 24;
    const logoTop = 8;
    let logoBottom = logoTop;
    if (societeLogo) {
      const format = societeLogo.startsWith('data:image/png') ? 'PNG'
        : societeLogo.startsWith('data:image/webp') ? 'WEBP'
        : 'JPEG';
      try {
        doc.addImage(societeLogo, format, 14, logoTop, logoSize, logoSize);
        logoBottom = logoTop + logoSize;
      } catch (e) {
        // Logo illisible (ex: SVG non supporté par jsPDF) : export sans logo plutôt que d'échouer.
      }
    }

    const infoX = 14 + logoSize + 6;
    let infoY = logoTop + 6;
    if (nomSociete) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(nomSociete, infoX, infoY);
      infoY += 5;
    }
    if (societeInfoLigne) {
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100);
      doc.text(societeInfoLigne, infoX, infoY);
      doc.setTextColor(0);
      infoY += 5;
    }

    let sousTitreY = Math.max(logoBottom, infoY) + 6;

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`Rapport — ${config.label}`, 148, sousTitreY, { align: 'center' });
    sousTitreY += 5;

    // Séparateur entre le bloc en-tête (logo + société + titre) et le reste.
    const sepY = sousTitreY + 3;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(14, sepY, 283, sepY);
    sousTitreY = sepY + 6;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} — ${lignes.length} ligne(s)`, 14, sousTitreY);

    autoTable(doc, {
      startY: sousTitreY + 4,
      head: [config.columns.map((c) => c.label)],
      body: lignes.map((row) => config.columns.map((c) => String(cellValue(row, c)))),
      theme: 'striped',
      headStyles: { fillColor: [11, 60, 93] },
      styles: { fontSize: 8 },
    });

    doc.save(`rapport_${domaine}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const exportExcel = () => {
    if (lignes.length === 0) {
      alert("Aucune donnée à exporter.");
      return;
    }
    const rows = lignes.map((row) => {
      const obj = {};
      config.columns.forEach((c) => { obj[c.label] = cellValue(row, c); });
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, config.label.substring(0, 31));
    XLSX.writeFile(wb, `rapport_${domaine}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (userInfo && userInfo.profile !== 1 && userInfo.profile !== 3) {
    return (
      <Layout>
        <div className="pagetitle"><h1>Rapports (stock, incidents, abonnements, quotas)</h1></div>
        <div className="alert alert-warning m-4">Réservé aux administrateurs et gestionnaires.</div>
      </Layout>
    );
  }

  const titrePage = userInfo?.profile === 1
    ? "Rapports (abonnements, quotas)"
    : "Rapports (stock, incidents, abonnements, quotas)";

  return (
    <Layout>
      <div className="pagetitle">
        <h1>{titrePage}</h1>
      </div>

      <section className="section">
        <div className="card mb-4" style={{ borderRadius: '10px', border: `1px solid ${COLORS.lightBg}` }}>
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label fw-semibold">Domaine</label>
                <select
                  className="form-select"
                  style={{ borderColor: '#c9d2dc' }}
                  value={domaine || ''}
                  onChange={(e) => setDomaine(e.target.value)}
                >
                  {Object.entries(DOMAINES)
                    .filter(([key]) => domainesAutorises.includes(key))
                    .map(([key, d]) => (
                      <option key={key} value={key}>{d.label}</option>
                    ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Du</label>
                <input type="date" className="form-control" style={{ borderColor: '#c9d2dc' }}
                  value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Au</label>
                <input type="date" className="form-control" style={{ borderColor: '#c9d2dc' }}
                  value={dateFin} onChange={(e) => setDateFin(e.target.value)} />
              </div>
              <div className="col-md-2">
                <button className="btn w-100" style={{ backgroundColor: COLORS.secondary, color: COLORS.white, borderRadius: '8px' }} onClick={fetchDonnees}>
                  <i className="bi bi-search me-1"></i> Filtrer
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <p className="text-muted mb-0">
            <i className={`bi ${config.icon} me-2`}></i>
            {lignes.length} ligne(s) — {config.label}
          </p>
          <div className="d-flex gap-2">
            <button className="btn" style={{ color: COLORS.danger, borderColor: COLORS.danger, backgroundColor: 'transparent', borderRadius: '8px' }} onClick={exportPDF}>
              <i className="bi bi-file-earmark-pdf me-2"></i> Exporter PDF
            </button>
            <button className="btn" style={{ color: COLORS.success, borderColor: COLORS.success, backgroundColor: 'transparent', borderRadius: '8px' }} onClick={exportExcel}>
              <i className="bi bi-file-earmark-excel me-2"></i> Exporter Excel
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5"><div className="spinner-border" style={{ color: COLORS.primary }}></div></div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle" style={{ backgroundColor: COLORS.white, borderRadius: '8px' }}>
              <thead>
                <tr style={{ backgroundColor: COLORS.lightBg }}>
                  {config.columns.map((c) => <th key={c.key}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {lignes.length === 0 ? (
                  <tr><td colSpan={config.columns.length} className="text-center text-muted py-4">Aucune donnée pour ce domaine.</td></tr>
                ) : (
                  lignes.map((row, idx) => (
                    <tr key={idx}>
                      {config.columns.map((c) => <td key={c.key}>{String(cellValue(row, c))}</td>)}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default Rapports;
